import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Top_Menu from '../main/Top_Menu';
import Bottom_Menu from '../main/Bottom_Menu';
import { auth, provider, db } from '../firebase';
import { signInWithPopup, signOut, onAuthStateChanged, User } from 'firebase/auth';
import { collection, addDoc, query, orderBy, onSnapshot, serverTimestamp, doc, updateDoc, deleteDoc } from 'firebase/firestore';

const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #F3EFE6;
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #5C4033;
  padding: 150px 5% 100px 5%; /* Top padding to account for fixed header */
`;

const Title = styled.h1`
  font-family: 'EB Garamond', serif;
  font-size: 2.8rem;
  font-weight: 500;
  letter-spacing: 0.2rem;
  margin-bottom: 4rem;
  color: #5C4033;
`;

const AdminFooter = styled.div`
  width: 100%;
  max-width: 1000px;
  display: flex;
  justify-content: flex-end;
  margin-top: 5rem;
`;

const AdminLoginButton = styled.button`
  background: none;
  border: none;
  color: #BCA18D;
  font-family: 'EB Garamond', serif;
  font-size: 0.7rem;
  letter-spacing: 0.05rem;
  cursor: pointer;
  padding: 0;
  transition: color 0.3s ease;

  &:hover {
    color: #8C7561;
  }
`;

const Button = styled.button`
  padding: 10px 20px;
  border: 1px solid #5C4033;
  background: transparent;
  color: #5C4033;
  font-family: 'EB Garamond', serif;
  font-size: 0.8rem;
  letter-spacing: 0.1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #5C4033;
    color: #F3EFE6;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FormContainer = styled.form`
  width: 100%;
  max-width: 1000px;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 4rem;
  padding: 3rem;
  background: #F3EFE6;
  box-sizing: border-box;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem;
  border: 1px solid #BCA18D;
  background: transparent;
  font-family: 'EB Garamond', serif;
  font-size: 1rem;
  color: #5C4033;
  outline: none;
  box-sizing: border-box;
`;

const ImageFieldRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const RemoveImageButton = styled.button`
  flex: 0 0 auto;
  border: none;
  background: transparent;
  color: #8C7561;
  cursor: pointer;
  font-family: 'EB Garamond', serif;
  font-size: 0.85rem;
  text-decoration: underline;
`;

const ImagePreviewGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 12px;
`;

const ImagePreviewItem = styled.div`
  position: relative;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  background: #E7E1D7;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  button {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 26px;
    height: 26px;
    border: none;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.65);
    color: white;
    cursor: pointer;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 1rem;
  border: 1px solid #BCA18D;
  background: transparent;
  font-family: 'EB Garamond', serif;
  font-size: 1rem;
  color: #5C4033;
  outline: none;
  min-height: 200px;
  resize: vertical;
  box-sizing: border-box;
`;

const NewsList = styled.div`
  width: 100%;
  max-width: 1000px;
  display: flex;
  flex-direction: column;
  gap: 3rem;
`;

const NewsItem = styled.div`
  display: flex;
  gap: 2rem;
  padding-bottom: 3rem;
  border-bottom: 1px solid transparent;
  cursor: pointer;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.8;
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ImageContainer = styled.div`
  flex: 0 0 45%;
  max-width: 450px;
`;

const NewsImage = styled.img`
  width: 100%;
  height: 300px;
  object-fit: cover;
  display: block;
`;

const TextContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const NewsTitle = styled.h2`
  font-family: 'EB Garamond', serif;
  font-size: 2.0rem;
  margin: 0 0 1.5rem 0;
  color: #5C4033;
  font-weight: 500;
`;

const NewsContent = styled.p`
  font-family: 'EB Garamond', serif;
  font-size: 1.05rem;
  line-height: 1.8;
  white-space: pre-wrap;
  color: #5C4033;
  margin: 0;
  opacity: 0.85;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const AdminActions = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 1rem;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: #8C7561;
  font-family: 'EB Garamond', serif;
  font-size: 0.8rem;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;

  &:hover {
    color: #5C4033;
  }
`;

// Detail View Styled Components
const DetailContainer = styled.div`
  width: 100%;
  max-width: 800px;
  display: flex;
  flex-direction: column;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #8C7561;
  font-family: 'EB Garamond', serif;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 0;
  margin-bottom: 2rem;
  text-align: left;
  display: inline-block;
  align-self: flex-start;

  &:hover {
    color: #5C4033;
    text-decoration: underline;
  }
`;

const DetailTitle = styled.h1`
  font-family: 'EB Garamond', serif;
  font-size: 2.8rem;
  margin: 0 0 1rem 0;
  color: #5C4033;
  font-weight: 500;
`;

const DetailDate = styled.span`
  font-family: 'EB Garamond', serif;
  font-size: 0.9rem;
  color: #8C7561;
  margin-bottom: 2rem;
`;

const DetailGallery = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DetailImage = styled.img`
  width: 100%;
  height: auto;
  max-height: 700px;
  object-fit: contain;
  display: block;

  &:only-child {
    grid-column: 1 / -1;
  }
`;

const DetailContent = styled.p`
  font-family: 'EB Garamond', serif;
  font-size: 1.15rem;
  line-height: 1.9;
  white-space: pre-wrap;
  color: #5C4033;
  margin: 0;
`;

const DetailBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const InlineDetailImage = styled.img`
  width: 100%;
  height: auto;
  max-height: 700px;
  object-fit: contain;
  display: block;
`;

interface Post {
  id: string;
  title: string;
  content: string;
  imageUrl?: string;
  imageUrls?: string[];
  createdAt: any;
}

const DEFAULT_IMAGE_URL = 'https://vuzagroup.synology.me/image/allta/';

const getPostImages = (post: Post): string[] => {
  const multipleImages = Array.isArray(post.imageUrls)
    ? post.imageUrls.filter(Boolean)
    : [];

  if (multipleImages.length > 0) return multipleImages;
  return post.imageUrl ? [post.imageUrl] : [];
};

const IMAGE_MARKER_PATTERN = /\{\{image:(\d+)\}\}/g;

const getPlacedImageIndexes = (content: string): Set<number> => {
  const indexes = new Set<number>();
  let match: RegExpExecArray | null;
  const matcher = new RegExp(IMAGE_MARKER_PATTERN.source, 'g');

  while ((match = matcher.exec(content)) !== null) {
    indexes.add(Number(match[1]) - 1);
  }

  return indexes;
};

const getUnplacedPostImages = (post: Post): string[] => {
  const images = getPostImages(post);
  const placedIndexes = getPlacedImageIndexes(post.content);
  return images.filter((_, index) => !placedIndexes.has(index));
};

const getContentPreview = (content: string): string =>
  content.replace(IMAGE_MARKER_PATTERN, '').replace(/\n{3,}/g, '\n\n').trim();

const renderPostContent = (post: Post): React.ReactNode => {
  const images = getPostImages(post);
  const parts = post.content.split(/(\{\{image:\d+\}\})/g);

  return parts.map((part, index) => {
    const markerMatch = part.match(/^\{\{image:(\d+)\}\}$/);
    if (markerMatch) {
      const imageIndex = Number(markerMatch[1]) - 1;
      const imageUrl = images[imageIndex];
      return imageUrl ? (
        <InlineDetailImage
          key={`inline-image-${imageIndex}-${index}`}
          src={imageUrl}
          alt={`${post.title} ${imageIndex + 1}`}
          loading="lazy"
        />
      ) : null;
    }

    if (!part.trim()) return null;
    return <DetailContent key={`content-${index}`}>{part.trim()}</DetailContent>;
  });
};

const News: React.FC = () => {
  const [user, setUser] = useState<User | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [imageUrls, setImageUrls] = useState<string[]>([DEFAULT_IMAGE_URL]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const contentInputRef = useRef<HTMLTextAreaElement | null>(null);
  const location = useLocation();

  const adminEmail = process.env.REACT_APP_ADMIN_EMAIL;

  useEffect(() => {
    if (location.state?.postId && posts.length > 0) {
      const post = posts.find(p => p.id === location.state.postId);
      if (post) {
        setSelectedPost(post);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [location.state, posts]);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });

    const q = query(collection(db, 'news'), orderBy('createdAt', 'desc'));
    const unsubscribeDb = onSnapshot(q, (snapshot) => {
      const postsData: Post[] = [];
      snapshot.forEach((doc) => {
        postsData.push({ id: doc.id, ...doc.data() } as Post);
      });
      setPosts(postsData);
    });

    return () => {
      unsubscribeAuth();
      unsubscribeDb();
    };
  }, []);

  const handleLogin = async () => {
    try {
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const resetForm = () => {
    setTitle('');
    setContent('');
    setImageUrls([DEFAULT_IMAGE_URL]);
    setEditingId(null);
    setIsFormVisible(false);
  };

  const updateImageUrl = (index: number, value: string) => {
    setImageUrls((current) => current.map((url, i) => i === index ? value : url));
  };

  const addImageUrlField = () => {
    setImageUrls((current) => [...current, DEFAULT_IMAGE_URL]);
  };

  const removeImageUrl = (index: number) => {
    const removedImageNumber = index + 1;
    setContent((current) => current
      .replace(new RegExp(`\\s*\\{\\{image:${removedImageNumber}\\}\\}\\s*`, 'g'), '\n')
      .replace(/\{\{image:(\d+)\}\}/g, (marker, number) => {
        const imageNumber = Number(number);
        return imageNumber > removedImageNumber
          ? `{{image:${imageNumber - 1}}}`
          : marker;
      })
      .replace(/\n{3,}/g, '\n\n'));
    setImageUrls((current) => {
      const next = current.filter((_, i) => i !== index);
      return next.length > 0 ? next : [DEFAULT_IMAGE_URL];
    });
  };

  const insertImageIntoContent = (index: number) => {
    const imageUrl = imageUrls[index]?.trim();
    if (!imageUrl || imageUrl === DEFAULT_IMAGE_URL) {
      alert('Please enter the complete image URL first.');
      return;
    }

    const marker = `{{image:${index + 1}}}`;
    if (content.includes(marker)) {
      alert('This image is already placed in the post.');
      return;
    }

    const textArea = contentInputRef.current;
    const start = textArea?.selectionStart ?? content.length;
    const end = textArea?.selectionEnd ?? start;
    const before = content.slice(0, start);
    const after = content.slice(end);
    const leadingBreak = before && !before.endsWith('\n') ? '\n' : '';
    const trailingBreak = after && !after.startsWith('\n') ? '\n' : '';
    const insertion = `${leadingBreak}${marker}${trailingBreak}`;
    const nextContent = `${before}${insertion}${after}`;
    const nextCursorPosition = before.length + insertion.length;

    setContent(nextContent);
    window.setTimeout(() => {
      contentInputRef.current?.focus();
      contentInputRef.current?.setSelectionRange(nextCursorPosition, nextCursorPosition);
    }, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !content) return alert("Please fill in the title and content.");

    setIsSubmitting(true);
    try {
      const enteredImageUrls = imageUrls
        .map((url) => url.trim())
        .filter((url) => url && url !== DEFAULT_IMAGE_URL);
      const postData = {
        title,
        content,
        imageUrls: enteredImageUrls,
        imageUrl: enteredImageUrls[0] || null,
      };

      if (editingId) {
        await updateDoc(doc(db, 'news', editingId), postData);
      } else {
        await addDoc(collection(db, 'news'), {
          ...postData,
          createdAt: serverTimestamp(),
        });
      }
      resetForm();
    } catch (error) {
      console.error("Error saving post:", error);
      alert("Failed to save post. Please ensure your Firestore permissions allow writes.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (e: React.MouseEvent, post: Post) => {
    e.stopPropagation(); // Prevent triggering selectedPost
    setTitle(post.title);
    setContent(post.content);
    const existingImages = getPostImages(post);
    setImageUrls(existingImages.length > 0 ? existingImages : [DEFAULT_IMAGE_URL]);
    setEditingId(post.id);
    setIsFormVisible(true);
    setSelectedPost(null); // Go back to list view to edit
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation(); // Prevent triggering selectedPost
    if (window.confirm("Are you sure you want to delete this post?")) {
      try {
        await deleteDoc(doc(db, 'news', id));
      } catch (error) {
        console.error("Error deleting post:", error);
        alert("Failed to delete post.");
      }
    }
  };

  const isAdmin = user && user.email === adminEmail;

  return (
    <Container>
      <Top_Menu />
      <Content>
        {!selectedPost && <Title>WHAT'S ON</Title>}

        {isAdmin && !selectedPost && (
          <div style={{ width: '100%', maxWidth: '1000px', display: 'flex', justifyContent: 'flex-end', marginBottom: isFormVisible || editingId ? '1rem' : '4rem' }}>
            {!editingId && (
              <Button onClick={() => setIsFormVisible(!isFormVisible)}>
                {isFormVisible ? 'CLOSE FORM' : '+ WRITE NEW POST'}
              </Button>
            )}
          </div>
        )}

        {isAdmin && !selectedPost && (isFormVisible || editingId) && (
          <FormContainer onSubmit={handleSubmit}>
            <h3 style={{ fontFamily: 'EB Garamond', margin: '0 0 1rem 0', fontSize: '1.5rem', fontWeight: 'normal' }}>
              {editingId ? 'Edit Post' : 'Write a Post'}
            </h3>
            <Input
              type="text"
              placeholder="Post Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {imageUrls.map((url, index) => (
                <ImageFieldRow key={index}>
                  <Input
                    type="url"
                    placeholder={`Image URL ${index + 1} (Optional)`}
                    value={url}
                    onChange={(e) => updateImageUrl(index, e.target.value)}
                  />
                  <Button type="button" onClick={() => insertImageIntoContent(index)} style={{ whiteSpace: 'nowrap' }}>
                    PLACE IN POST
                  </Button>
                  <RemoveImageButton type="button" onClick={() => removeImageUrl(index)}>
                    REMOVE
                  </RemoveImageButton>
                </ImageFieldRow>
              ))}
              <Button type="button" onClick={addImageUrlField} style={{ alignSelf: 'flex-start' }}>
                + ADD IMAGE URL
              </Button>
            </div>
            {imageUrls.some((url) => url.trim() && url.trim() !== DEFAULT_IMAGE_URL) && (
              <ImagePreviewGrid>
                {imageUrls.map((url, index) => {
                  const trimmedUrl = url.trim();
                  if (!trimmedUrl || trimmedUrl === DEFAULT_IMAGE_URL) return null;
                  return (
                    <ImagePreviewItem key={`url-${index}`}>
                      <img src={trimmedUrl} alt={`URL preview ${index + 1}`} />
                      <button type="button" aria-label={`Remove image ${index + 1}`} onClick={() => removeImageUrl(index)}>&times;</button>
                    </ImagePreviewItem>
                  );
                })}
              </ImagePreviewGrid>
            )}
            <p style={{ margin: '-0.5rem 0 0', color: '#8C7561', fontSize: '0.9rem' }}>
              Place the cursor where the image should appear, then select PLACE IN POST.
            </p>
            <TextArea
              ref={contentInputRef}
              placeholder="Post Content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
            />
            <div style={{ display: 'flex', gap: '10px' }}>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'SAVING...' : (editingId ? 'UPDATE POST' : 'SAVE POST')}
              </Button>
              {editingId ? (
                <Button
                  type="button"
                  onClick={resetForm}
                >
                  CANCEL
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={resetForm}
                >
                  CANCEL
                </Button>
              )}
            </div>
          </FormContainer>
        )}

        {selectedPost ? (
          <DetailContainer>
            <BackButton onClick={() => setSelectedPost(null)}>← BACK TO LIST</BackButton>
            <DetailTitle>{selectedPost.title}</DetailTitle>
            <DetailDate>
              {selectedPost.createdAt ? new Date(selectedPost.createdAt.seconds * 1000).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              }) : ''}
            </DetailDate>
            {getUnplacedPostImages(selectedPost).length > 0 && (
              <DetailGallery>
                {getUnplacedPostImages(selectedPost).map((image, index) => (
                  <DetailImage
                    key={`${image}-${index}`}
                    src={image}
                    alt={`${selectedPost.title} ${index + 1}`}
                    loading="lazy"
                  />
                ))}
              </DetailGallery>
            )}
            <DetailBody>{renderPostContent(selectedPost)}</DetailBody>

            {isAdmin && (
              <AdminActions style={{ marginTop: '3rem', borderTop: '1px solid #BCA18D', paddingTop: '1rem' }}>
                <ActionButton onClick={(e) => handleEdit(e, selectedPost)}>Edit</ActionButton>
                <ActionButton onClick={(e) => { handleDelete(e, selectedPost.id); setSelectedPost(null); }}>Delete</ActionButton>
              </AdminActions>
            )}
          </DetailContainer>
        ) : (
          <NewsList>
            {posts.map((post) => {
              const postImages = getPostImages(post);
              return (
              <NewsItem key={post.id} onClick={() => setSelectedPost(post)}>
                {postImages.length > 0 && (
                  <ImageContainer>
                    <NewsImage src={postImages[0]} alt={post.title} loading="lazy" />
                  </ImageContainer>
                )}
                <TextContainer>
                  <NewsTitle>{post.title}</NewsTitle>
                  <NewsContent>{getContentPreview(post.content)}</NewsContent>
                  {isAdmin && (
                    <AdminActions>
                      <ActionButton onClick={(e) => handleEdit(e, post)}>Edit</ActionButton>
                      <ActionButton onClick={(e) => handleDelete(e, post.id)}>Delete</ActionButton>
                    </AdminActions>
                  )}
                </TextContainer>
              </NewsItem>
              );
            })}
            {posts.length === 0 && (
              <p style={{ fontFamily: 'EB Garamond', textAlign: 'center', opacity: 0.6 }}>No news available at the moment.</p>
            )}
          </NewsList>
        )}

        {!selectedPost && (
          <AdminFooter>
            {user ? (
              <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                <span style={{ fontFamily: 'EB Garamond', fontSize: '0.7rem', color: '#BCA18D' }}>{user.email}</span>
                <AdminLoginButton onClick={handleLogout}>LOGOUT</AdminLoginButton>
              </div>
            ) : (
              <AdminLoginButton onClick={handleLogin}>ADMIN LOGIN</AdminLoginButton>
            )}
          </AdminFooter>
        )}
      </Content>
      <Bottom_Menu />
    </Container>
  );
};

export default News;
